/**
 * Feature request API calls. Query keys start with the live entity key
 * `feature_request`, so another user's change refetches the list (SSE).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OFFICE_HEADER } from '@velora/contracts';
import {
  FeedbackAttachmentSchema,
  FeedbackDetailSchema,
  FeedbackListResponse,
  type FeedbackAttachment,
  type FeedbackCreateBody,
  type FeedbackPriority,
  type FeedbackStatus,
  type FeedbackType,
} from '@velora/contracts/feedback';
import { clientFor, csrfHeaders } from '../../app/api.js';
import { useOfficeQuery } from '../../app/shell.js';

export const LIVE_KEY = 'feature_request';

export interface ListFilters {
  status: FeedbackStatus | 'active' | 'all';
  type: FeedbackType | null;
  area: string | null;
}

export function listPath(f: ListFilters): string {
  const s = new URLSearchParams({ status: f.status });
  if (f.type) s.set('type', f.type);
  if (f.area) s.set('area', f.area);
  return `/feedback/requests?${s.toString()}`;
}

export function useRequests(f: ListFilters) {
  return useOfficeQuery({
    queryKey: [LIVE_KEY, 'list', f.status, f.type, f.area],
    queryFn: (client, signal) =>
      client.request(listPath(f), { schema: FeedbackListResponse, signal }),
    placeholderData: (prev) => prev,
  });
}

export function useRequest(id: string | null) {
  return useOfficeQuery({
    queryKey: [LIVE_KEY, 'detail', id],
    queryFn: (client, signal) =>
      client.request(`/feedback/requests/${id ?? ''}`, { schema: FeedbackDetailSchema, signal }),
    enabled: id !== null,
  });
}

/** Files a request in one office (`office` is an office id). */
export function createRequest(office: string, body: FeedbackCreateBody) {
  return clientFor(office).request('/feedback/requests', {
    method: 'POST',
    body,
    schema: FeedbackDetailSchema,
  });
}

/** Same-origin URL of an attachment's bytes (office header via query). */
export function attachmentUrl(id: string, office: string): string {
  return `/v1/feedback/attachments/${id}/content?office=${encodeURIComponent(office)}`;
}

export async function uploadAttachment(
  office: string,
  requestId: string,
  file: File,
  noteId?: string,
): Promise<FeedbackAttachment> {
  const s = new URLSearchParams({ filename: file.name.slice(0, 200) || 'attachment' });
  if (noteId) s.set('noteId', noteId);
  const res = await fetch(`/v1/feedback/requests/${requestId}/attachments?${s.toString()}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/octet-stream',
      [OFFICE_HEADER]: office,
      ...csrfHeaders(),
    },
    body: file,
  });
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg =
      (body as { error?: { message?: string } } | null)?.error?.message ??
      `Upload failed (${res.status})`;
    throw new Error(msg);
  }
  return FeedbackAttachmentSchema.parse(body);
}

export function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ predicate: (q) => q.queryKey.includes(LIVE_KEY) });
}

export function useAddNote(office: string, id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ note, files }: { note: string; files: readonly File[] }) => {
      const d = await clientFor(office).request(`/feedback/requests/${id}/notes`, {
        method: 'POST',
        body: { note },
        schema: FeedbackDetailSchema,
      });
      const last = [...d.notes].reverse().find((n) => n.kind === 'note');
      for (const f of files) await uploadAttachment(office, id, f, last?.id);
      return d;
    },
    onSettled: invalidate,
  });
}

export function useSetStatus(office: string, id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { status: FeedbackStatus; priority: FeedbackPriority }) =>
      clientFor(office).request(`/feedback/requests/${id}/status`, {
        method: 'POST',
        body,
        schema: FeedbackDetailSchema,
      }),
    onSettled: invalidate,
  });
}
